import type { Ref } from 'vue'
import { ref, watch, onMounted, onUnmounted } from 'vue'
import {
  deleteQueryParam,
  setQueryParam,
  getQueryParam,
} from '@app/utils/url-helpers'

export default function useUrlStorage<T>(
  key: string,
  initialValue: T,
  serialize: (value: T) => string,
  { syncToUrl = true }: { syncToUrl?: boolean } = {}
): Ref<T> {
  const value = ref<T>(initialValue) as Ref<T>

  if (!syncToUrl) return value

  // Initialize from URL if present
  const readFromUrl = (): void => {
    const param = getQueryParam(key)
    if (param) {
      value.value = param as unknown as T
    }
  }
  readFromUrl()

  watch(
    value,
    (newValue) => {
      const serialized = serialize(newValue)
      if (serialized !== '') {
        setQueryParam(key, serialized, { replace: true })
      } else {
        deleteQueryParam(key, { replace: true })
      }
    },
    { deep: true }
  )

  // Handle browser back/forward
  onMounted(() => window.addEventListener('popstate', readFromUrl))
  onUnmounted(() => window.removeEventListener('popstate', readFromUrl))

  return value
}
